import { isMobile } from 'helpers/PlatformHelpers';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ScreenSize } from 'types';

const getViewportDimensions = () => {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
};

const useViewportDimensions = () => {
  const [viewportDimensions, setViewportDimensions] = useState(getViewportDimensions());
  const [isAppMobile, setIsAppMobile] = useState(false);

  const screenSize: ScreenSize = useMemo(() => {
    const { width } = viewportDimensions;

    const bodyStyles = document.body.style;
    bodyStyles.setProperty('--app-mode-mobile', width < 768 ? 'true' : 'false');
    document.body.setAttribute('data-app-mode-mobile', width < 768 ? 'true' : 'false');

    if (width < 475) return ScreenSize.Small;
    if (width < 768) return ScreenSize.Medium;
    if (width < 1024) return ScreenSize.Large;
    if (width < 1440) return ScreenSize.ExtraLarge;
    if (width < 1920) return ScreenSize.ExtraExtraLarge;

    return ScreenSize.Large;
  }, [viewportDimensions]);

  const debounceTimeout = useRef<number | null>(null);

  useEffect(() => {
    function handleResize() {
      if (debounceTimeout.current !== null) {
        window.clearTimeout(debounceTimeout.current);
      }
      debounceTimeout.current = window.setTimeout(() => {
        setViewportDimensions(getViewportDimensions());
        const mobile = isMobile();
        setIsAppMobile(mobile);
        debounceTimeout.current = null;
      }, 150); // 150ms debounce delay
    }

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (debounceTimeout.current !== null) {
        window.clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    setIsAppMobile(screenSize === ScreenSize.Small || screenSize === ScreenSize.Medium);
  }, [screenSize]);

  return { viewportDimensions, isAppMobile, screenSize };
};

export default useViewportDimensions;
